import React, { useState } from 'react';
import { ReferenceAsset } from '../types';
import { X, Check, Search } from 'lucide-react';

interface AssetPickerModalProps {
  assets: ReferenceAsset[];
  selectedIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
}

const AssetPickerModal: React.FC<AssetPickerModalProps> = ({ assets, selectedIds, isOpen, onClose, onConfirm }) => {
  const [tempSelected, setTempSelected] = useState<string[]>(selectedIds);
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  // Sync state on open (simplified for this structure, relying on parent to reset if needed)
  // Ideally use useEffect to sync when isOpen changes to true.
  
  const toggleAsset = (id: string) => {
    setTempSelected(prev => 
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const handleConfirm = () => {
    onConfirm(tempSelected);
    onClose();
  };

  const filteredAssets = assets.filter(a => 
    a.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    a.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-2xl flex flex-col max-h-[80vh] shadow-2xl">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">选择参考资源</h3>
            <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded-full text-slate-400">
                <X size={20} />
            </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-800 bg-slate-900">
            <div className="relative">
                <Search className="absolute left-3 top-2.5 text-slate-500" size={16} />
                <input 
                    type="text" 
                    placeholder="搜索资源..." 
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg pl-9 pr-4 py-2 text-sm text-slate-200 focus:border-indigo-500 outline-none"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {filteredAssets.map(asset => {
                    const isSelected = tempSelected.includes(asset.id);
                    return (
                        <div 
                            key={asset.id}
                            onClick={() => toggleAsset(asset.id)}
                            className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                                isSelected ? 'border-indigo-500 ring-2 ring-indigo-500/50' : 'border-slate-800 hover:border-slate-600'
                            }`}
                        >
                            <img src={asset.url} className="w-full h-full object-cover" alt={asset.name} />
                            
                            {/* Overlay info */}
                            <div className="absolute inset-x-0 bottom-0 bg-black/60 p-1.5">
                                <p className="text-[10px] text-white truncate font-medium">{asset.name}</p>
                                <span className={`text-[9px] uppercase font-bold ${
                                    asset.type === 'character' ? 'text-indigo-300' :
                                    asset.type === 'scene' ? 'text-emerald-300' : 'text-orange-300'
                                }`}>{asset.type}</span>
                            </div>

                            {/* Checkmark */}
                            {isSelected && (
                                <div className="absolute top-2 right-2 bg-indigo-600 text-white p-1 rounded-full shadow-lg">
                                    <Check size={12} strokeWidth={3} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {filteredAssets.length === 0 && (
                <p className="text-center text-slate-500 py-8">未找到资源。</p>
            )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 flex justify-end gap-3 bg-slate-900 rounded-b-xl">
            <span className="text-xs text-slate-500 self-center mr-auto">
                已选 {tempSelected.length} 个资源 (建议最多 8 个)
            </span>
            <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:bg-slate-800 rounded-lg text-sm">取消</button>
            <button onClick={handleConfirm} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-bold shadow-lg">
                确认选择
            </button>
        </div>
      </div>
    </div>
  );
};

export default AssetPickerModal;